import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ChartTooltip } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';

const COLORS = ['#0A1A2F', '#1C485C', '#3D8C91', '#89CDD4', '#D8FAF8'];

export default function TieringSnapshot({ accounts, isLoading }) {
  const { t } = useTranslation();
  
  const getTierData = () => {
    const tiers = { '1': 0, '2': 0, '3': 0, '4': 0 };

      // DEBUG: Check first account
  if (accounts.length > 0) {
    console.log('🔍 First account:', {
      name: accounts[0].account_name,
      account_tier: accounts[0].account_tier,
      tier: accounts[0].tier,
      account_fit_score: accounts[0].account_fit_score
    });
  }
    
    accounts.forEach(acc => {
      let tierNum;
      
      // Check if tier was manually overridden
      const tierValue = acc.account_tier || acc.account_tier;
      if (tierValue) {
        // Extract number from "Tier 1", "T1", etc.
        const match = tierValue.toString().match(/(\d)/);
        if (match) {
          tierNum = match[1];
        }
      }
      
      // If no manual tier, calculate from score
      if (!tierNum) {
        const isAplus = acc.account_fit_score >= 90 && acc.decision_window_band === '<12m';
        
        if (isAplus || acc.account_fit_score >= 90) {
          tierNum = '1';
        } else if (acc.account_fit_score >= 75) {
          tierNum = '2';
        } else if (acc.account_fit_score >= 60) {
          tierNum = '3';
        } else {
          tierNum = '4';
        }
      }
      
      if (tiers.hasOwnProperty(tierNum)) {
        tiers[tierNum]++;
      }
    });

    return Object.entries(tiers)
      .map(([name, value]) => ({ name, value }))
      .filter(d => d.value > 0)
      .sort((a, b) => parseInt(a.name) - parseInt(b.name));
  };
  
  const data = isLoading ? [] : getTierData();

  return (
    <Card className="border-0 shadow-md h-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('tieringSnapshot')}</CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="ml-1 p-1 rounded-full hover:bg-gray-100 transition-colors cursor-help"
                  aria-label={t('tieringSnapshot')}
                >
                  <Info className="w-4 h-4 text-[#5C6B7A]" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-[#0A1A2F] text-white border-0 shadow-lg">
                <p className="text-sm">
                  {t('tieringTooltip')}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          {isLoading ? <Skeleton className="w-48 h-48 rounded-full mx-auto" /> : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={70} labelLine={false} label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                  const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
                  const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                  const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
                  return (
                    <text x={x} y={y} fill="#5C6B7A" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12}>
                      {`${(percent * 100).toFixed(0)}%`}
                    </text>
                  );
                }}>
                {data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
              </Pie>
              <ChartTooltip />
            </PieChart>
          </ResponsiveContainer>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
          {data.map((entry, index) => (
            <div key={entry.name} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
              <span className="text-xs text-[#5C6B7A]">{t('tier')} {entry.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}